import "../assets/styles/overview.css";
import { useState, useEffect, useCallback } from "react"; // Added useCallback
import { isMobile } from "react-device-detect";

// Assuming these images are directly importable or in your public folder
import NextArrow from "../assets/images/next.png";
import PrevArrow from "../assets/images/previous.png";
import PlaceholderImage from "../assets/images/placeholder.jpg"; // Your fallback image

function PaintingOverview(props) {
  const { paintings, category_name } = props;

  // State to manage the currently viewed full-screen painting
  // We'll store the entire painting object directly, which simplifies access
  const [currentFullScreenPainting, setCurrentFullScreenPainting] =
    useState(null);

  // State to manage the index of the full-screen painting within the 'paintings' array
  const [fullScreenPaintingIndex, setFullScreenPaintingIndex] = useState(-1); // -1 indicates no painting is full screen

  const minSwipeDistance = 50;
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Effect to update currentFullScreenPainting when index or paintings array changes
  useEffect(() => {
    if (
      paintings &&
      paintings.length > 0 &&
      fullScreenPaintingIndex >= 0 &&
      fullScreenPaintingIndex < paintings.length
    ) {
      setCurrentFullScreenPainting(paintings[fullScreenPaintingIndex]);
    } else {
      // If index is out of bounds or paintings array is empty, clear full screen
      setCurrentFullScreenPainting(null);
    }
  }, [fullScreenPaintingIndex, paintings]);

  // Handler to display a painting in full screen
  const displayFullScreenImage = useCallback(
    (painting) => {
      // Find the index of the clicked painting
      const index = paintings.findIndex((p) => p._id === painting._id);
      if (index !== -1) {
        setFullScreenPaintingIndex(index);
      }
      // setCurrentFullScreenPainting(painting); // Redundant due to useEffect above
    },
    [paintings]
  ); // Depend on paintings array

  // Handler to hide the full screen view
  const hideFullScreen = useCallback(() => {
    setCurrentFullScreenPainting(null);
    setFullScreenPaintingIndex(-1); // Reset index
  }, []);

  // Navigation handlers
  const navigatePainting = useCallback(
    (direction) => {
      let newIndex = fullScreenPaintingIndex;
      if (direction === "next") {
        newIndex = Math.min(paintings.length - 1, fullScreenPaintingIndex + 1);
      } else if (direction === "prev") {
        newIndex = Math.max(0, fullScreenPaintingIndex - 1);
      }
      setFullScreenPaintingIndex(newIndex);
    },
    [fullScreenPaintingIndex, paintings.length]
  );

  // Touch handlers for swipe navigation
  const onTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (touchStart === null || touchEnd === null) return; // Guard against incomplete touches
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      navigatePainting("next");
    } else if (isRightSwipe) {
      navigatePainting("prev");
    }
    setTouchStart(null); // Reset touch states
    setTouchEnd(null);
  };

  // Image error handler
  const handleError = (e, paintingName) => {
    e.target.onerror = null; // Prevent endless loop
    e.target.src = PlaceholderImage; // Use the imported placeholder
    console.error(`Failed to load image for "${paintingName}". Check URL.`);
  };

  // Determine if there are previous/next paintings for navigation buttons
  const hasPrev = fullScreenPaintingIndex > 0;
  const hasNext = fullScreenPaintingIndex < paintings.length - 1;

  return (
    <div className="portfolio-container">
      <div className="category-name">{category_name}</div>{" "}
      {/* Destructured props.category_name */}
      <div className="portfolio-paintings-container">
        {paintings.map((painting) => (
          <div className="portfolio-individual-container" key={painting._id}>
            <img
              onClick={() => displayFullScreenImage(painting)}
              src={painting.imageUrl}
              alt={painting.name || "Painting"}
              onError={(e) => handleError(e, painting.name)}
            />
          </div>
        ))}
      </div>
      {/* Conditional rendering for the full-screen modal */}
      {currentFullScreenPainting && (
        <div
          className="full-screen-image-container"
          // Consider if clicking background should close the modal; current setup does not
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="full-screen-image-inner-container">
            <div className="close" onClick={hideFullScreen}>
              X
            </div>
            <div className="image-carousel-container">
              <img
                id="full-image"
                src={currentFullScreenPainting.imageUrl}
                alt={currentFullScreenPainting.name || "Full Screen Painting"}
                onError={(e) => handleError(e, currentFullScreenPainting.name)}
              />
              {!isMobile && (
                <>
                  {hasPrev && (
                    <div
                      className="previous"
                      onClick={() => navigatePainting("prev")}
                    >
                      <img src={PrevArrow} alt="Previous"></img>
                    </div>
                  )}
                  {hasNext && (
                    <div
                      className="next"
                      onClick={() => navigatePainting("next")}
                    >
                      <img src={NextArrow} alt="Next"></img>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="fullscreen-painting-info">
              <div className="fullscreen-painting-title">
                {currentFullScreenPainting.name}
              </div>
              <div className="fullscreen-painting-medium">
                {currentFullScreenPainting.medium}
              </div>
              <div className="fullscreen-painting-size">
                {currentFullScreenPainting.size}
              </div>
              {currentFullScreenPainting.colour && (
                <div className="fullscreen-painting-colour">
                  {currentFullScreenPainting.colour}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PaintingOverview;
